/**
 * MiningSystem — directional, cone-based hull mining. The hull's beams split
 * across the nearest asteroids inside its cone + range: with N beams and K rocks
 * in range, up to N rocks are mined at once (nearest first; leftover beams double
 * up on the closest rocks). Each rock mines at (its beams × beamRate).
 *
 * Per-rock state: a wandering impact point (re-rolled every wanderInterval) and a
 * throttled "+N" mineral tick. Impact points track their rock as it orbits.
 */
import { Vector2 } from "@/math/Vector2";
import { MINING_LASER } from "@/config/balance";
import type { Mothership } from "@/game/mothership/Mothership";
import type { MineableBody } from "@/game/world/body";

export interface MiningTargetFrame {
  id: string;
  impacts: Vector2[]; // one live world-space impact point PER beam (each wanders)
  beams: number; // hull beams assigned to it (= impacts.length)
}

export interface MiningFrame {
  muzzle: Vector2; // beam origin (front node of the hull)
  targets: MiningTargetFrame[]; // 0..beamCount rocks being mined this tick
}

interface ImpactState {
  offset: Vector2; // impact offset relative to the rock centre
  wanderT: number;
  impact: Vector2; // reused world-space point
}

export class MiningSystem {
  private impacts = new Map<string, ImpactState>();
  private floats = new Map<string, { accum: number; timer: number }>();

  update(
    dt: number,
    ship: Mothership,
    bodies: MineableBody[],
    depositMinerals: (amount: number) => void,
    miningRange: number,
    beamRate: number,
    beamCount: number,
    emitFloater: (x: number, y: number, amount: number) => void,
    omni = false, // Mk IV Omni-Mining Array → mine a full 360° circle
  ): MiningFrame {
    const forward = Vector2.fromAngle(ship.heading);
    const muzzle = ship.pos.add(forward.scale(MINING_LASER.muzzleOffset));
    const cosHalf = Math.cos((MINING_LASER.coneHalfAngleDeg * Math.PI) / 180);

    // In-range rocks, nearest first (front cone only, unless Omni-Mining is on).
    const cands: { body: MineableBody; dist: number }[] = [];
    for (const p of bodies) {
      if (p.ore <= 0) continue;
      const toP = p.pos.sub(ship.pos);
      const dist = toP.length();
      if (dist < 1e-3 || dist > miningRange + p.radius) continue;
      if (!omni && forward.dot(toP.scale(1 / dist)) < cosHalf) continue;
      cands.push({ body: p, dist });
    }
    cands.sort((a, b) => a.dist - b.dist);

    const beams = Math.max(1, Math.floor(beamCount));
    const targetCount = Math.min(cands.length, beams);
    if (targetCount === 0) {
      this.impacts.clear();
      this.floats.clear();
      return { muzzle, targets: [] };
    }

    // Spread beams over the nearest `targetCount` rocks; nearest get the extras.
    const base = Math.floor(beams / targetCount);
    const rem = beams % targetCount;

    const targets: MiningTargetFrame[] = [];
    const alive = new Set<string>(); // body ids still mined (float-tick cleanup)
    const aliveKeys = new Set<string>(); // per-beam impact keys still in use
    for (let i = 0; i < targetCount; i++) {
      const body = cands[i]!.body;
      const nBeams = base + (i < rem ? 1 : 0);
      alive.add(body.id);

      // One INDEPENDENT wandering impact spot per beam, scattered over the rock —
      // so each beam visibly strikes a different point in the target's area.
      const spots: Vector2[] = [];
      for (let slot = 0; slot < nBeams; slot++) {
        const key = `${body.id}#${slot}`;
        let st = this.impacts.get(key);
        if (!st || st.wanderT >= MINING_LASER.wanderInterval) {
          const t = Math.random() * Math.PI * 2;
          const rr =
            Math.sqrt(Math.random()) * body.radius * MINING_LASER.impactRadiusFrac;
          st = {
            offset: new Vector2(Math.cos(t) * rr, Math.sin(t) * rr),
            // stagger the first re-roll so beams don't all jump in unison
            wanderT: st ? 0 : Math.random() * MINING_LASER.wanderInterval,
            impact: new Vector2(),
          };
          this.impacts.set(key, st);
        }
        st.wanderT += dt;
        st.impact.set(body.pos.x + st.offset.x, body.pos.y + st.offset.y);
        spots.push(st.impact);
        aliveKeys.add(key);
      }

      // Mine at nBeams × beamRate.
      const got = Math.min(nBeams * beamRate * dt, body.ore);
      body.ore -= got;
      depositMinerals(got);

      // Throttled "+N" tick per rock (shown at the first beam's spot).
      let fs = this.floats.get(body.id);
      if (!fs) {
        fs = { accum: 0, timer: 0 };
        this.floats.set(body.id, fs);
      }
      fs.accum += got;
      fs.timer += dt;
      if (fs.timer >= 0.3 && fs.accum > 0.5) {
        const s0 = spots[0]!;
        emitFloater(s0.x, s0.y, fs.accum);
        fs.accum = 0;
        fs.timer = 0;
      }

      targets.push({ id: body.id, impacts: spots, beams: nBeams });
    }

    // Drop state for beams / rocks no longer targeted.
    for (const k of this.impacts.keys()) if (!aliveKeys.has(k)) this.impacts.delete(k);
    for (const id of this.floats.keys()) if (!alive.has(id)) this.floats.delete(id);

    return { muzzle, targets };
  }

  reset(): void {
    this.impacts.clear();
    this.floats.clear();
  }
}
